import { Responsive } from '@cian/ui-kit';
import { Body2, UIHeading3 } from '@cian/ui-kit/typography';
import * as React from 'react';

import * as styles from './NewbuildingCardMobile.css';
// oxlint-disable-next-line cian-eslint-compat/no-restricted-paths
import { callbackIfIdle } from '../../../browser/utils/callbackIfIdle';
import { NewbuildingCallButtonContainer } from '../../containers/NewbuildingCallButtonContainer';
import { NewbuildingComparisonButtonContainer } from '../../containers/NewbuildingComparisonButtonContainer';
import { ShareContainer } from '../../containers/ShareContainer';
import { useFavoritesUxfeedbackCampaign } from '../../hooks/uxfeedback';
import { IFavoriteEntityMedia } from '../../types/results';
import { Card, ICardProps } from '../Card';
import { trackCardClick } from '../Card/tracking';
import { CardActionsGroup } from '../CardActionsGroup';
import { CardGallery } from '../CardGallery';
import { Favorites } from '../Favorites';
import { LinkArea } from '../LinkArea';
import { trackCall, trackPhoneButtonClick } from '../Phone/tracking';
import { PublicationRemovalNotice } from '../PublicationRemovalNotice';
import { Undergrounds } from '../Undergrounds';

const CALL_TRACKING_DELAY = 10000;

export const NewbuildingCardMobile: React.FC<ICardProps> = props => {
  const { favoriteEntity, page, position, total, removeFavorite } = props;
  const { sendFavoritesUxfeedbackCampaign } = useFavoritesUxfeedbackCampaign();

  const {
    bargainTerms,
    comparisonStatus,
    deleteDate,
    entityId,
    fullUrl,
    geo,
    jkInfo,
    media,
    newbuildingDynamicCalltracking,
    phone,
    photoFullUrl,
    photoPreviewUrl,
    status,
    title,
  } = favoriteEntity;

  const trackMediaClick = React.useCallback(() => {
    trackCardClick({ entity: favoriteEntity, position, total, deviceType: page.deviceType, label: 'card_photo' });
  }, [favoriteEntity, page.deviceType, position, total]);

  const trackTitleClick = React.useCallback(() => {
    trackCardClick({ entity: favoriteEntity, position, total, deviceType: page.deviceType, label: 'card_header' });
  }, [favoriteEntity, page.deviceType, position, total]);

  const trackCallClick = React.useCallback(() => {
    trackPhoneButtonClick({ entity: favoriteEntity, position, total, deviceType: page.deviceType });
    callbackIfIdle(
      () => trackCall({ entity: favoriteEntity, position, total, deviceType: page.deviceType }),
      CALL_TRACKING_DELAY,
    );
  }, [favoriteEntity, page, position, total]);

  const handleRemove = React.useCallback(() => {
    removeFavorite(entityId);
    sendFavoritesUxfeedbackCampaign();
  }, [entityId, removeFavorite, sendFavoritesUxfeedbackCampaign]);

  const galleryMedia = React.useMemo<IFavoriteEntityMedia[]>(() => {
    if (media?.length) {
      return media;
    }

    return photoPreviewUrl && photoFullUrl ? [{ previewUrl: photoPreviewUrl, fullUrl: photoFullUrl }] : [];
  }, [media, photoFullUrl, photoPreviewUrl]);

  const address = geo.address.map(({ title: addressTitle }) => addressTitle).join(', ');

  return (
    <>
      <Responsive phone>
        <div className={styles['card']} data-testid="favorite-offer-card">
          <LinkArea className={styles['media']} href={fullUrl} onClick={trackMediaClick}>
            <div data-testid="card_image">
              <CardGallery media={galleryMedia} alt="Главное фото ЖК">
                <CardActionsGroup
                  compare={
                    comparisonStatus && (
                      <NewbuildingComparisonButtonContainer
                        entityId={entityId}
                        label={fullUrl}
                        comparisonStatus={comparisonStatus}
                        appearance="icon"
                      />
                    )
                  }
                  favorite={<Favorites isMobileActionGroup onClick={handleRemove} />}
                  share={<ShareContainer title={title} url={fullUrl} />}
                />
              </CardGallery>
            </div>
            <PublicationRemovalNotice status={status} deleteDate={deleteDate} />
          </LinkArea>

          <LinkArea className={styles['content']} href={fullUrl} onClick={trackTitleClick}>
            <div className={styles['facts']}>
              {bargainTerms.priceText && <UIHeading3>{bargainTerms.priceText}</UIHeading3>}
              <Body2>{title}</Body2>
              {jkInfo?.deadlineText && <Body2 color="text-secondary-default">{jkInfo.deadlineText}</Body2>}
            </div>

            <div className={styles['geo']}>
              {geo.undergrounds.length > 0 && <Undergrounds undergrounds={geo.undergrounds.slice(0, 1)} />}
              {address && <Body2>{address}</Body2>}
            </div>
          </LinkArea>

          <NewbuildingCallButtonContainer
            entityId={entityId}
            phone={phone}
            siteBlockId={newbuildingDynamicCalltracking?.siteBlockId}
            onClick={trackCallClick}
          />
        </div>
      </Responsive>

      <Responsive tablet>
        <Card {...props} />
      </Responsive>
    </>
  );
};

NewbuildingCardMobile.displayName = 'NewbuildingCardMobile';
